import { useTranslation } from 'react-i18next';
import PropTypes, { bool } from 'prop-types';
import classnames from 'classnames';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Paper from '@material-ui/core/Paper';
import ImageDisplay from './ImageDisplay';
import { color } from '../../app/theme';

const useStyles = makeStyles(
  theme => ({
    paper: {
      borderRadius: '12px',
      boxShadow:
        '0 16px 48px 0 rgba(0, 0, 0, 0.07), 0 6px 18px 0 rgba(0, 0, 0, 0.03)',
    },
    popup: {
      position: 'absolute',
      top: 48,
      left: 0,
      right: 0,
      zIndex: 2,
    },
    body1: {
      ...theme.typography.body1,
      display: 'flex',
      alignItems: 'center',
      margin: '15px 0px 16px 0px',
      cursor: 'pointer',
      '&:hover': {
        color: color.$2,
      },
    },
    body2: theme.typography.body2,
    platform: {
      position: 'relative',
      zIndex: '1',
    },
    title: {
      marginLeft: '8px',
      fontSize: '15px',
    },
    menuContainer: {
      padding: '12px 24px 8px 24px',
    },
    badge: {
      display: 'block',
    },
    extraBadge: {
      display: 'block',
      margin: '0 0 0 12px',
    },
  }),
  {
    name: 'AutocompletePanel',
  },
);

const AutocompletePanel = ({
  dataArray,
  headerData,
  searchTerm,
  value,
  header,
  extraContent,
  handleClick,
  style,
  extraPicture,
  headerStyle,
  popup,
  body1Class,
  body2Class,
  menuContainerClass,
  paperClass,
  titleClass,
  isTrans,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const getFilterData = () => {
    if (searchTerm) {
      return dataArray;
    }
    return dataArray.filter(d => d.objectId !== headerData);
  };

  return (
    <Paper
      className={classnames(paperClass, {
        [classes.paper]: true,
        [classes.popup]: popup,
      })}
      style={style}
    >
      <div className={classnames(classes.menuContainer, menuContainerClass)}>
        {headerData && !searchTerm && (
          <div
            className={classnames(classes.body2, body2Class)}
            style={headerStyle}
          >
            {header}
            {dataArray
              .filter(d => d.objectId === headerData)
              .map((item, idx) => (
                <div
                  key={`AutocompletePanel-${item.objectId ?? idx}`}
                  className={classnames(classes.body1, body1Class)}
                  onClick={() => handleClick(item)}
                >
                  {extraContent && (
                    <span
                      style={{ color: color.$13 }}
                    >{`${extraContent}`}</span>
                  )}
                  {extraPicture && (
                    <ImageDisplay
                      alt={item.title}
                      style={{
                        display: 'block',
                        objectFit: 'cover',
                        cursor: 'pointer',
                        borderRadius: '50%',
                      }}
                      src={item.picture}
                      width={32}
                      height={32}
                      badgeClass={classes.extraBadge}
                    />
                  )}
                  <span className={classnames(classes.title, titleClass)}>
                    {isTrans ? t(item[value]) : item[value]}
                  </span>
                </div>
              ))}
          </div>
        )}

        {getFilterData().map((item, idx) => (
          <div
            key={`AutocompletePanel-${item.objectId ?? idx}`}
            className={classnames(classes.body1, body1Class)}
            style={{
              marginTop:
                (!headerData || searchTerm) && headerStyle && idx === 0
                  ? '0'
                  : '',
            }}
            onClick={() => handleClick(item)}
          >
            {extraContent && (
              <span style={{ color: color.$13 }}>{`${extraContent}`}</span>
            )}
            {extraPicture && (
              <ImageDisplay
                alt={item.title}
                style={{
                  display: 'block',
                  objectFit: 'cover',
                  cursor: 'pointer',
                  borderRadius: '50%',
                }}
                src={item.picture}
                width={32}
                height={32}
                badgeClass={classes.extraBadge}
              />
            )}
            <span className={classnames(classes.title, titleClass)}>
              {isTrans ? t(item[value]) : item[value]}
            </span>
          </div>
        ))}
      </div>
    </Paper>
  );
};

AutocompletePanel.defaultProps = {
  body1Class: undefined,
  menuContainerClass: undefined,
  popup: false,
  headerData: '',
  searchTerm: '',
  titleClass: undefined,
  isTrans: false,
};

AutocompletePanel.propTypes = {
  body1Class: PropTypes.string,
  menuContainerClass: PropTypes.string,
  popup: bool,
  headerData: PropTypes.string,
  searchTerm: PropTypes.string,
  titleClass: PropTypes.string,
  isTrans: PropTypes.bool,
};

export default AutocompletePanel;
